import { createSvgEl } from '../svg';
import type { PiecePlan, PiecePose } from '@learnforge/demo-math/geometry';
import type { Vec2 } from '@learnforge/demo-math/geometry';

export interface DissectionOptions {
  plan: PiecePlan;
  width: number;
  height: number;
}

export interface DissectionHandle {
  group: SVGGElement;
  setT(t: number): void;
  destroy(): void;
}

/**
 * Linear interpolation between two values.
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Linear interpolation of a 2D vector.
 */
function lerpVec2(a: Vec2, b: Vec2, t: number): Vec2 {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
  };
}

/**
 * Linear interpolation of a PiecePose.
 */
function lerpPose(start: PiecePose, end: PiecePose, t: number): PiecePose {
  return {
    translate: lerpVec2(start.translate, end.translate, t),
    rotateDeg: lerp(start.rotateDeg, end.rotateDeg, t),
    about: lerpVec2(start.about, end.about, t),
  };
}

/**
 * Format a Vec2 as SVG polygon points string.
 */
function pointsString(polygon: readonly Vec2[]): string {
  return polygon.map((p) => `${p.x},${p.y}`).join(' ');
}

/**
 * Create an SVG transform attribute from a PiecePose.
 * Format: "translate(tx ty) rotate(deg cx cy)"
 */
function poseToTransform(pose: PiecePose): string {
  const { translate, rotateDeg, about } = pose;
  return `translate(${translate.x} ${translate.y}) rotate(${rotateDeg} ${about.x} ${about.y})`;
}

export function createDissection(parent: SVGElement, options: DissectionOptions): DissectionHandle {
  const { plan } = options;

  // Create the main container group
  const group = createSvgEl('g');
  parent.appendChild(group);

  // Create a piece group for each piece in the plan
  const pieceElements: SVGGElement[] = [];

  plan.pieces.forEach((piece, index) => {
    // Each piece gets its own group with a CSS class for theming
    const pieceGroup = createSvgEl('g', {
      'data-role': 'piece',
      'data-id': piece.id,
      class: `geo-piece-${index % 10}`, // Cycle through palette classes 0-9
    });
    group.appendChild(pieceGroup);

    // Create a polygon for this piece. The host group is often scaled (e.g. by
    // GRID_UNIT), which would scale the stroke too and produce a fat border —
    // `non-scaling-stroke` keeps the outline a constant 1px regardless of scale.
    const polygon = createSvgEl('polygon', {
      points: pointsString(piece.polygon),
      'vector-effect': 'non-scaling-stroke',
    });
    pieceGroup.appendChild(polygon);

    pieceElements.push(pieceGroup);
  });

  // setT updates all piece transforms based on interpolation parameter
  const setT = (t: number): void => {
    const clampedT = Math.max(0, Math.min(1, t));

    for (let i = 0; i < plan.pieces.length; i++) {
      const piece = plan.pieces[i]!;
      const pieceGroup = pieceElements[i]!;

      // Interpolate between start and end poses
      const pose = lerpPose(piece.start, piece.end, clampedT);

      // Apply the transform
      pieceGroup.setAttribute('transform', poseToTransform(pose));
    }
  };

  // Initialize at t=0
  setT(0);

  return {
    group,
    setT,
    destroy(): void {
      if (group.parentNode) {
        group.parentNode.removeChild(group);
      }
    },
  };
}
